import React from "react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import PageHead from "@/components/PageHead";
import { ArrowUpRight, Phone } from "@phosphor-icons/react";

const RIGHTS = [
  {
    n: "01",
    t: "Right to know",
    d: "Request a copy of the categories of personal information we have collected about you and the categories of sources and recipients.",
  },
  {
    n: "02",
    t: "Right to delete",
    d: "Request deletion of personal information we have collected about you, subject to limited regulatory exceptions (audit, fraud prevention, legal obligations).",
  },
  {
    n: "03",
    t: "Right to correct",
    d: "Request that we correct inaccurate personal information we maintain about you.",
  },
  {
    n: "04",
    t: "Right to opt out",
    d: "Opt out of the sale or sharing of your personal information.",
  },
  {
    n: "05",
    t: "Right to non-discrimination",
    d: "We will not discriminate against you for exercising any of your CCPA / CPRA rights.",
  },
];

export default function CCPA() {
  return (
    <main data-testid="ccpa-page">
      <PageHead
        title="CCPA Opt-Out & Privacy Rights"
        description="Exercise your California Consumer Privacy Act rights with Citirex — opt out of sale or sharing, request access, deletion or correction."
        path="/ccpa-opt-out"
      />

      <section className="border-b border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-12 md:px-12 md:pb-24 md:pt-20">
          <Reveal>
            <p className="overline">CCPA · CPRA</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 max-w-5xl font-display text-5xl font-semibold leading-[0.95] tracking-tighter md:text-7xl lg:text-[96px]">
              Your privacy{" "}
              <span className="font-editorial italic font-normal text-citirex-accent">
                rights
              </span>
              .
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-citirex-sub md:text-xl">
              If you are a California resident — or a resident of a state
              with comparable privacy law — you have the right to opt out of
              the sale or sharing of your personal information and to make
              additional requests under the CCPA / CPRA.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <p className="overline">Your rights at a glance</p>
          <div className="mt-10 grid grid-cols-1 gap-px bg-citirex-border md:grid-cols-2 lg:grid-cols-5">
            {RIGHTS.map((r) => (
              <div key={r.n} className="bg-citirex-bg p-6 md:p-8">
                <p className="font-editorial text-sm italic text-citirex-sub">
                  {r.n}
                </p>
                <h3 className="mt-2 font-display text-lg font-medium text-citirex-ink">
                  {r.t}
                </h3>
                <p className="mt-3 font-body text-sm text-citirex-sub">
                  {r.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-citirex-border bg-citirex-bg2">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 md:grid-cols-12 md:gap-16 md:px-12 md:py-32">
          <div className="md:col-span-7">
            <p className="overline">Submit a request</p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] md:text-5xl">
              Opt out, or make a{" "}
              <span className="font-editorial italic">privacy request</span>.
            </h2>
            <p className="mt-6 max-w-xl font-body text-base text-citirex-sub md:text-lg">
              Use the contact form on our partnerships page and select{" "}
              <strong className="font-semibold text-citirex-ink">
                Compliance
              </strong>{" "}
              as the department. Include your full name, state of residence
              and the type of request (opt-out, know, delete, correct). We
              respond to verified requests within 45 days as required by law.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="ccpa-cta-form"
                className="group inline-flex items-center gap-2 bg-citirex-ink px-7 py-5 font-body text-sm font-semibold uppercase tracking-wider text-citirex-inv transition-colors hover:bg-citirex-accent"
              >
                Open the contact form
                <ArrowUpRight
                  size={18}
                  weight="bold"
                  className="transition-transform group-hover:rotate-45"
                />
              </Link>
              <a
                href="tel:+18188532332"
                data-testid="ccpa-cta-phone"
                className="group inline-flex items-center gap-2 border-2 border-citirex-ink px-7 py-5 font-body text-sm font-semibold uppercase tracking-wider text-citirex-ink transition-colors hover:bg-citirex-ink hover:text-citirex-inv"
              >
                <Phone size={16} weight="bold" />
                (818) 853-2332
              </a>
            </div>
          </div>

          <aside className="md:col-span-5">
            <div className="border border-citirex-border bg-citirex-surface p-8">
              <p className="overline">By mail</p>
              <p className="mt-4 font-display text-lg font-medium leading-snug text-citirex-ink">
                Citirex LLC<br />
                Attn: Privacy<br />
                15165 Ventura Blvd, Suite 225<br />
                Sherman Oaks, CA 91403
              </p>
              <p className="mt-6 font-body text-xs text-citirex-sub">
                Written requests should include your full name, state of
                residence, request type and a return mailing address so we
                can verify your identity before responding.
              </p>
            </div>

            <div className="mt-6 border border-citirex-border bg-citirex-ink p-8 text-citirex-inv">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-citirex-inv/60">
                Authorized agents
              </p>
              <p className="mt-3 font-body text-sm text-citirex-inv/80">
                Requests submitted by an authorized agent must include
                signed authorization from the consumer. We may contact the
                consumer directly to verify the request.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
